// apps/radar/src/routes/sources.tsx
// Version: 2026.4.24

import { createFileRoute } from '@tanstack/react-router';
import {
  EnginePageShell, GlassPanel, SectionLabel, StatusBadge, LoadingShimmer,
  EmptyState, C, F,
} from '../components/engine';
import { useSources } from '../lib/api/hooks';
import type { LegalRisk } from '@forgetrack/api-client';

export const Route = createFileRoute('/sources')({ component: SourcesPage });

const RISK_COLOR = (risk: LegalRisk) =>
  risk === 'Low' ? C.neon : risk === 'Medium' ? C.amber : C.coral;

function SourcesPage() {
  const { data, loading, error } = useSources();

  return (
    <EnginePageShell
      title="Sources"
      subtitle="CRAWLER REGISTRY"
      icon="📡"
      accentColor={C.violet}
      brand="FORGERADAR"
      footer="ForgeRadar · Edson Technologies · forgeradar.app"
    >
      <GlassPanel accent={C.violet}>
        <SectionLabel color={C.violet}>Phase 1 Sources</SectionLabel>

        {error && (
          <div style={{ padding: '24px 0', color: C.coral }}>
            Failed to load sources: {error.message}
          </div>
        )}

        {!error && loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0' }}>
            {Array.from({ length: 8 }).map((_, i) => <LoadingShimmer key={i} height={32} />)}
          </div>
        )}

        {!error && !loading && (data?.length ?? 0) === 0 && (
          <EmptyState
            icon="📡"
            title="No sources configured"
            subtitle="Run the Phase 0 source-validation pass and seed the Sources table to get going."
          />
        )}

        {!error && !loading && data && data.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{
                textAlign: 'left', color: C.muted, fontSize: 11,
                fontFamily: F.mono, textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>
                <th style={th}>Source</th>
                <th style={th}>Family</th>
                <th style={th}>Legal Risk</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Items 24h</th>
                <th style={{ ...th, textAlign: 'right' }}>Error %</th>
              </tr>
            </thead>
            <tbody>
              {data.map(s => (
                <tr key={s.sourceCode} style={{ fontSize: 14, color: C.text }}>
                  <td style={{ ...td, fontFamily: F.mono }}>{s.sourceCode}</td>
                  <td style={{ ...td, color: C.muted }}>{s.family}</td>
                  <td style={td}>
                    <StatusBadge label={s.legalRisk} color={RISK_COLOR(s.legalRisk)} />
                  </td>
                  <td style={td}>
                    <StatusBadge
                      label={s.active ? 'Active' : 'Pending'}
                      color={s.active ? C.neon : C.dim}
                      glow={s.active}
                    />
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: F.mono, color: C.cyan }}>
                    {s.itemsLast24h}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: F.mono,
                                color: s.errorRateLast24h > 0.1 ? C.coral : C.dim }}>
                    {(s.errorRateLast24h * 100).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassPanel>
    </EnginePageShell>
  );
}

const th = { padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.18)' };
const td = { padding: '12px 8px' };
